import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Section, Container } from '@/components/shared/Section';
import { GlassPanel } from '@/components/shared/GlassPanel';
import { SignalChip } from '@/components/shared/SignalChip';
import { AnimatedReveal } from '@/components/shared/AnimatedReveal';
import { SEO } from '@/components/shared/SEO';
import { fadeInUp, staggerContainer, viewportOnce } from '@/lib/motion';
import { Eye, DollarSign, Smartphone, Search, ChevronRight, BarChart3 } from 'lucide-react';

const solutions = [
  {
    title: 'Meta Ads Intelligence',
    desc: 'Monitor every Facebook and Instagram ad your competitors run. Track creatives, copy, targeting signals, and campaign timing.',
    href: '/solutions/meta-ads',
    badge: 'Meta',
    icon: Eye,
    stats: '5.2M+ ads tracked',
  },
  {
    title: 'TikTok Ads Intelligence',
    desc: 'Track TikTok ad creatives, trends, and competitor campaign strategies. Detect new campaigns the moment they launch.',
    href: '/solutions/tiktok-ads',
    badge: 'TikTok',
    icon: Smartphone,
    stats: '3.1M+ ads tracked',
  },
  {
    title: 'Google Ads Intelligence',
    desc: 'Analyze competitor search and display ad strategies across Google networks. Track keywords, copy, and spend patterns.',
    href: '/solutions/google-ads',
    badge: 'Google',
    icon: Search,
    stats: '4.8M+ ads tracked',
  },
  {
    title: 'Competitor Pricing Intelligence',
    desc: 'Monitor competitor pricing, stock levels, and discount strategies in real time. Get alerted when competitors undercut you.',
    href: '/solutions/competitor-pricing',
    badge: 'Pricing',
    icon: DollarSign,
    stats: '50K+ products monitored',
  },
];

export default function SolutionsNew() {
  return (
    <>
      <SEO
        title="Solutions"
        description="RADAR gives you deep competitive visibility across advertising, pricing, and technology — all in one platform built for Shopify merchants."
        canonical="/solutions"
      />
      {/* Hero */}
      <Section hero className="relative overflow-hidden">
        <div className="absolute inset-0 radar-glow-hero opacity-50" />
        <Container className="relative z-10">
          <AnimatedReveal>
            <p className="text-xs font-mono uppercase tracking-wider text-lime-500 mb-3">Solutions</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight text-foreground mb-4">
              Intelligence for every channel
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-[560px] mb-8">
              RADAR gives you deep competitive visibility across advertising, pricing, and technology — all in one platform built for Shopify merchants.
            </p>
          </AnimatedReveal>
        </Container>
      </Section>

      {/* Solution Cards */}
      <Section dense>
        <Container>
          <motion.div
            className="grid md:grid-cols-2 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            data-testid="solutions-hub"
          >
            {solutions.map((s, i) => (
              <motion.div key={i} variants={fadeInUp}>
                <Link to={s.href}>
                  <GlassPanel hover className="p-7 sm:p-8 h-full flex flex-col group">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-lime-500/10 border border-lime-500/20">
                        <s.icon className="h-5 w-5 text-lime-500" />
                      </div>
                      <SignalChip variant="lime">{s.badge}</SignalChip>
                    </div>
                    <h2 className="text-xl font-display font-bold text-foreground mb-2">{s.title}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-6">{s.desc}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-mono text-lime-400">{s.stats}</p>
                      <span className="flex items-center gap-1.5 text-sm font-medium text-lime-400 group-hover:gap-2.5 transition-all">
                        Explore <ChevronRight className="h-4 w-4" />
                      </span>
                    </div>
                  </GlassPanel>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* Why choose RADAR */}
      <Section className="border-t border-white/[0.04]">
        <Container>
          <AnimatedReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-foreground mb-4">
                Why teams choose RADAR
              </h2>
              <p className="text-base text-muted-foreground max-w-[480px] mx-auto">
                One platform replaces multiple tools. One dashboard replaces hours of manual research.
              </p>
            </div>
          </AnimatedReveal>
          <motion.div className="grid sm:grid-cols-3 gap-6" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce}>
            {[
              { val: '10M+', label: 'Ads tracked monthly', icon: BarChart3 },
              { val: '24/7', label: 'Real-time monitoring', icon: Eye },
              { val: '<5min', label: 'Alert delivery time', icon: Smartphone },
            ].map((m, i) => (
              <motion.div key={i} variants={fadeInUp}>
                <GlassPanel className="p-6 text-center">
                  <m.icon className="h-6 w-6 text-lime-500 mx-auto mb-3" />
                  <p className="text-3xl font-display font-bold text-foreground mb-1">{m.val}</p>
                  <p className="text-sm text-muted-foreground">{m.label}</p>
                </GlassPanel>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* CTA */}
      <Section>
        <Container>
          <AnimatedReveal>
            <GlassPanel className="p-10 sm:p-14 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-lime-500/[0.03] to-transparent pointer-events-none" />
              <div className="relative z-10">
                <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">Ready to see what your competitors are doing?</h2>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">Start your 7-day free trial. No credit card required.</p>
                <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors shadow-[0_0_30px_rgba(204,255,108,0.2)]">
                  Start free trial <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </GlassPanel>
          </AnimatedReveal>
        </Container>
      </Section>
    </>
  );
}
